import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import AdminLayout from "@/components/admin/AdminLayout"
import { getAdminMembers } from "@/api/adminMemberApi"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"

type AdminMember = {
    memberId: number
    loginId: string
    memberName: string
    email: string
    phone: string
    role: string
    status: string
    createdAt: string
}

export default function AdminMemberPage() {
    const navigate = useNavigate()

    const [members, setMembers] = useState<AdminMember[]>([])
    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState({
        searchKeyword: "",
        status: "",
    })

    // 💡 1. 페이징 관련 상태 추가
    const [page, setPage] = useState(0) // 현재 페이지 (0부터 시작)
    const [totalPages, setTotalPages] = useState(0) // 전체 페이지 수
    const [totalElements, setTotalElements] = useState(0) // 전체 회원 수
    const pageSize = 10 // 한 페이지당 표시할 개수

    // 💡 2. 목록 조회 함수 수정 (targetPage 인자 추가 및 응답 데이터 매핑)
    const fetchMembers = async (targetPage = page) => {
        try {
            setLoading(true)
            const response = await getAdminMembers({
                searchKeyword: search.searchKeyword || undefined,
                status: search.status || undefined,
                page: targetPage,
                size: pageSize,
            })

            const responseData = response.data || response

            setMembers(responseData.items || [])
            setTotalPages(responseData.totalPages || 0)
            setTotalElements(responseData.totalElements || 0)
            setPage(targetPage)
        } catch (error) {
            console.error(error)
            alert("회원 목록 조회 중 오류가 발생했습니다.")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchMembers(0)
    }, [])

    // 💡 3. 검색 시 1페이지(0번 인덱스)부터 조회하도록 연동
    const handleSearch = () => {
        fetchMembers(0)
    }

    // 💡 4. 페이지 이동 핸들러
    const handlePageChange = (newPage: number) => {
        if (newPage < 0 || newPage >= totalPages) return
        fetchMembers(newPage)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') handleSearch()
    }

    return (
      <AdminLayout>
          <div className="space-y-6">
              <div>
                  <h2 className="text-3xl font-bold">회원 관리</h2>
                  <p className="mt-2 text-sm text-slate-500">
                      가입된 회원의 정보와 상태를 관리합니다.
                  </p>
              </div>

              <Button
                className="rounded-xl bg-slate-900 hover:bg-slate-800"
                onClick={() => navigate("/admin/members/new")}
              >
                  신규 회원 등록
              </Button>

              <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                  <CardHeader>
                      <CardTitle className="text-xl">회원 검색</CardTitle>
                  </CardHeader>
                  <CardContent>
                      <div className="grid gap-3 md:grid-cols-[1fr_180px_120px]">
                          <Input
                            placeholder="이름 또는 아이디 검색"
                            className="rounded-xl"
                            value={search.searchKeyword}
                            onChange={(e) => setSearch((prev) => ({ ...prev, searchKeyword: e.target.value }))}
                            onKeyDown={handleKeyDown}
                          />
                          <select
                            className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none"
                            value={search.status}
                            onChange={(e) => setSearch((prev) => ({ ...prev, status: e.target.value }))}
                          >
                              <option value="">전체 상태</option>
                              <option value="ACTIVE">활성 (ACTIVE)</option>
                              <option value="INACTIVE">비활성 (INACTIVE)</option>
                              <option value="SLEEP">휴면 (SLEEP)</option>
                          </select>
                          <Button className="rounded-xl" onClick={handleSearch}>검색</Button>
                      </div>
                  </CardContent>
              </Card>

              <Card className="rounded-[28px] border border-slate-200 shadow-sm overflow-hidden">
                  <CardContent className="p-0">
                      <div className="overflow-x-auto">
                          <table className="w-full min-w-[980px] text-sm text-left">
                              <thead>
                              <tr className="border-b border-slate-200 bg-slate-50 text-slate-500">
                                  <th className="px-4 py-4 font-medium">No.</th>
                                  <th className="px-4 py-4 font-medium">아이디</th>
                                  <th className="px-4 py-4 font-medium">이름</th>
                                  <th className="px-4 py-4 font-medium">이메일</th>
                                  <th className="px-4 py-4 font-medium">연락처</th>
                                  <th className="px-4 py-4 font-medium">권한</th>
                                  <th className="px-4 py-4 font-medium">상태</th>
                                  <th className="px-4 py-4 font-medium">가입일</th>
                              </tr>
                              </thead>
                              <tbody>
                              {loading ? (
                                <tr>
                                    <td colSpan={8} className="px-4 py-8 text-center text-slate-500">조회 중...</td>
                                </tr>
                              ) : members.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="px-4 py-8 text-center text-slate-500">등록된 회원이 없습니다.</td>
                                </tr>
                              ) : (
                                members.map((member) => (
                                  <tr
                                    key={member.memberId}
                                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                                    onClick={() => navigate(`/admin/members/${member.memberId}`)}
                                  >
                                      <td className="px-4 py-4">{member.memberId}</td>
                                      <td className="px-4 py-4 font-medium text-slate-900">{member.loginId}</td>
                                      <td className="px-4 py-4">{member.memberName}</td>
                                      <td className="px-4 py-4">{member.email}</td>
                                      <td className="px-4 py-4">{member.phone || "-"}</td>
                                      <td className="px-4 py-4">
                                                <span className={`rounded-full px-3 py-1 text-xs font-medium ${member.role === 'ADMIN' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-700'}`}>
                                                    {member.role}
                                                </span>
                                      </td>
                                      <td className="px-4 py-4">
                                                <span className={`rounded-full px-3 py-1 text-xs font-medium ${member.status === 'ACTIVE' ? 'bg-blue-100 text-blue-700' : 'bg-rose-100 text-rose-700'}`}>
                                                    {member.status}
                                                </span>
                                      </td>
                                      <td className="px-4 py-4 text-slate-500">{member.createdAt.split(' ')[0]}</td>
                                  </tr>
                                ))
                              )}
                              </tbody>
                          </table>
                      </div>

                      {/* 💡 5. 페이징 네비게이션 UI 추가 */}
                      {totalPages > 0 && (
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-slate-100 bg-white">
                            <div className="text-sm text-slate-500">
                                총 <span className="font-semibold text-slate-900">{totalElements}</span>명의 회원 (페이지 {page + 1} / {totalPages})
                            </div>

                            <div className="flex items-center gap-1.5">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="rounded-lg h-9 px-3 text-xs"
                                  disabled={page === 0 || loading}
                                  onClick={() => handlePageChange(page - 1)}
                                >
                                    이전
                                </Button>

                                <div className="flex items-center gap-1">
                                    {Array.from({ length: totalPages }, (_, i) => i).map((p) => {
                                        if (
                                          totalPages > 7 &&
                                          Math.abs(p - page) > 2 &&
                                          p !== 0 &&
                                          p !== totalPages - 1
                                        ) {
                                            if (p === 1 || p === totalPages - 2) {
                                                return (
                                                  <span key={p} className="px-1 text-xs text-slate-400">
                                                            ...
                                                        </span>
                                                )
                                            }
                                            return null
                                        }

                                        return (
                                          <Button
                                            key={p}
                                            variant={p === page ? "default" : "ghost"}
                                            size="sm"
                                            className={`h-8 w-8 rounded-lg p-0 text-xs font-medium ${
                                              p === page
                                                ? "bg-slate-900 text-white hover:bg-slate-800"
                                                : "text-slate-600 hover:bg-slate-100"
                                            }`}
                                            onClick={() => handlePageChange(p)}
                                            disabled={loading}
                                          >
                                              {p + 1}
                                          </Button>
                                        )
                                    })}
                                </div>

                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="rounded-lg h-9 px-3 text-xs"
                                  disabled={page >= totalPages - 1 || loading}
                                  onClick={() => handlePageChange(page + 1)}
                                >
                                    다음
                                </Button>
                            </div>
                        </div>
                      )}
                  </CardContent>
              </Card>
          </div>
      </AdminLayout>
    )
}